import { Loader2 } from 'lucide-react'

export type StageState = 'pending' | 'active' | 'done'

interface StageIconProps {
  state: StageState
  /**
   * Optional aria-label override. Defaults are "Pending", "In progress",
   * "Done"; pass a more specific label when the state is meaningful for
   * screen-reader users (e.g. inside a stages list, the wrapper row
   * usually carries the meaning so the icon stays aria-hidden).
   */
  label?: string
}

/**
 * 16px slot for progress-feed markers. Three states:
 *
 *   pending — empty box (paper-faint hairline)
 *   active  — acid border + acid glow + spinner child
 *   done    — filled green check pill
 *
 * Pairs with the `.stage-marker` and `.check-pill` recipes in
 * `design-system.css`. Used by onboarding's `ScanningStep` discovery
 * feed and the Sunday Studio's drafting feed in the cockpit. The check
 * polyline matches the `taka-icn-check` symbol in `OnboardingShell.tsx`
 * so the visual reads identically; this primitive ships the polyline
 * inline so it works on any surface, not only inside the onboarding
 * sprite-bearing wrapper.
 */
export function StageIcon({ state, label }: StageIconProps) {
  if (state === 'done') {
    return (
      <span className="check-pill" aria-label={label ?? 'Done'} role="img">
        <svg viewBox="0 0 16 16" aria-hidden>
          <polyline points="3.5 8.5 6.8 11.6 12.5 5" />
        </svg>
      </span>
    )
  }
  if (state === 'active') {
    return (
      <span
        className="stage-marker"
        data-state="active"
        aria-label={label ?? 'In progress'}
        role="img"
      >
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.4} />
      </span>
    )
  }
  return (
    <span
      className="stage-marker"
      data-state="pending"
      aria-label={label ?? 'Pending'}
      role="img"
    />
  )
}
